import axios from "axios";
import { API_URL } from "./baseUrl";
import TokenService from "./token-service";
import { getUserTypeID } from "./common";
import { normalizeToE164 } from "./phoneUtils";

function authHeaders() {
  const token = TokenService.getLocalAccessToken();
  if (!token) return {};
  return {
    "x-access-token": token,
    "user-type": getUserTypeID(),
    "user-id": localStorage.getItem("user_id") || "",
  };
}

/**
 * Format phone number to E.164 format
 * @param {string} phone - Phone number to format
 * @returns {string|null} - Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return null;
  return normalizeToE164(phone) || null;
};

function parseSendSmsResponse(response, { requireSms = false } = {}) {
  const payload = response?.data || {};
  const otpChannel = String(
    payload.otp_channel || payload.otp_required_channel || payload.data?.otp_channel || "",
  )
    .trim()
    .toLowerCase();
  const smsFallback = Boolean(
    payload.sms_fallback || payload.data?.sms_fallback,
  );

  if (requireSms && (smsFallback || otpChannel === "email")) {
    return {
      success: false,
      message:
        payload.message ||
        "SMS could not be delivered to this number. Please check the country code and number.",
      smsFallback: true,
      otpChannel,
      data: payload.data,
    };
  }

  if (response.status === 200 && payload.status === 1) {
    return {
      success: true,
      message: payload.message || "OTP sent successfully",
      data: payload.data,
      otpChannel: otpChannel || "sms",
      smsFallback,
      smsSkipped: Boolean(payload.sms_skipped || payload.data?.sms_skipped),
      cooldownSeconds:
        payload.cooldown_seconds ?? payload.data?.cooldown_seconds ?? null,
    };
  }

  return {
    success: false,
    message: payload.message || "Failed to send OTP",
    data: payload.data,
  };
}

/**
 * Send SMS OTP to phone number
 * @param {string} phone - Phone number in E.164 format
 * @param {string} email - User email (optional)
 * @param {Object} [options]
 * @param {boolean} [options.requireSms] - Fail when backend falls back to email OTP
 * @returns {Promise<Object>} - Response object with success status and message
 */
export const sendSMSOTP = async (phone, email = null, options = {}) => {
  try {
    const formattedPhone = formatPhoneNumber(phone);

    if (!formattedPhone) {
      return {
        success: false,
        message: "Invalid phone number",
      };
    }

    const response = await axios.post(
      `${API_URL}auth/send-sms-otp`,
      {
        phone: formattedPhone,
        email: email ? String(email).trim() : null,
      },
      {
        withCredentials: true,
        headers: authHeaders(),
      },
    );

    return parseSendSmsResponse(response, options);
  } catch (error) {
    console.error("Error sending SMS OTP:", error);
    const errorMessage =
      error.response?.data?.message ||
      "Failed to send OTP. Please try again.";
    return {
      success: false,
      message: errorMessage,
    };
  }
};

/**
 * Verify SMS OTP
 * @param {string} otp - OTP code to verify
 * @param {string} phone - Phone number in E.164 format
 * @param {string} email - User email (optional)
 * @param {Object} [options]
 * @param {boolean} [options.rememberBrowser]
 * @param {string} [options.deviceId]
 * @returns {Promise<Object>} - Response object with success status and user data
 */
export const verifySMSOTP = async (otp, phone, email = null, options = {}) => {
  try {
    const formattedPhone = formatPhoneNumber(phone);

    if (!formattedPhone) {
      return {
        success: false,
        message: "Invalid phone number",
      };
    }

    const payload = {
      otp: otp,
      phone: formattedPhone,
      email: email ? String(email).trim() : null,
    };

    if (options.rememberBrowser) {
      payload.remember_browser = true;
      payload.device_id = options.deviceId || null;
      payload.trusted_days = options.trustedDays || 7;
    }

    const response = await axios.post(
      `${API_URL}auth/verify-sms-otp`,
      payload,
      {
        withCredentials: true,
        headers: authHeaders(),
      },
    );

    if (response.status === 200 && response.data.status === 1) {
      const data = response.data.data?.[0] || response.data.data || {};
      return {
        success: true,
        message: response.data.message || "OTP verified successfully",
        user: data,
        trustedDeviceToken:
          data.trusted_device_token ||
          response.data.trusted_device_token ||
          null,
        trustedDeviceExpiresAt:
          data.trusted_device_expires_at ||
          response.data.trusted_device_expires_at ||
          null,
        deviceId:
          data.device_id ||
          response.data.device_id ||
          options.deviceId ||
          null,
      };
    }

    return {
      success: false,
      message: response.data.message || "Invalid OTP",
    };
  } catch (error) {
    console.error("Error verifying SMS OTP:", error);
    const errorMessage =
      error.response?.data?.message ||
      "Failed to verify OTP. Please try again.";
    return {
      success: false,
      message: errorMessage,
    };
  }
};
